import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Plus,
  Settings,
  Copy,
  Archive,
  MoreVertical,
  Layers,
  Calendar,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const PipelinesTab = ({ 
  token, 
  agencyId = null, 
  isAgency = false,
  basePath = '/platform/opportunities',
}) => {
  const navigate = useNavigate();
  const [pipelines, setPipelines] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPipelines();
  }, [token, agencyId]);

  const fetchPipelines = async () => {
    setLoading(true);
    try {
      const endpoint = isAgency 
        ? `${API}/agency/pipelines`
        : `${API}/platform/pipelines`;
      
      const headers = {
        Authorization: `Bearer ${token}`,
      };
      if (agencyId) {
        headers['X-Agency-ID'] = agencyId;
      }

      const response = await axios.get(endpoint, { headers });
      setPipelines(response.data.pipelines || []);
    } catch (err) {
      console.error('Failed to fetch pipelines:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditPipeline = (pipeline) => {
    navigate(`${basePath}/pipelines/${pipeline.id}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="pipelines-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500">
            {pipelines.length} pipeline{pipelines.length !== 1 ? 's' : ''} available
          </p>
        </div>
        <Button className="bg-emerald-600 hover:bg-emerald-700">
          <Plus className="mr-2 h-4 w-4" />
          Create Pipeline
        </Button>
      </div>

      {/* Pipeline List */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {/* Table Header */}
        <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-medium text-slate-600 uppercase tracking-wider">
          <div className="col-span-4">Pipeline Name</div>
          <div className="col-span-2">Stages</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-2">Updated</div>
          <div className="col-span-2">Actions</div>
        </div>

        {/* Table Body */}
        {pipelines.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <Layers className="mx-auto h-10 w-10 text-slate-300 mb-3" />
            <p className="font-medium">No pipelines found</p>
            <p className="text-sm mt-1">Create your first pipeline to get started</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {pipelines.map((pipeline) => (
              <div
                key={pipeline.id}
                className="grid grid-cols-12 gap-4 px-4 py-3 items-center hover:bg-slate-50 transition-colors"
                data-testid={`pipeline-row-${pipeline.id}`}
              >
                {/* Name */}
                <div className="col-span-4">
                  <p className="font-medium text-stone-900">{pipeline.name}</p>
                  {pipeline.description && (
                    <p className="text-xs text-slate-500 truncate">{pipeline.description}</p>
                  )}
                </div>

                {/* Stages */}
                <div className="col-span-2 flex items-center gap-2">
                  <Layers className="h-4 w-4 text-slate-400" />
                  <span className="text-sm text-stone-700">
                    {pipeline.stages?.length || 0} stages
                  </span>
                </div>

                {/* Type */}
                <div className="col-span-2">
                  {pipeline.is_platform_template ? (
                    <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                      Template
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs">
                      Custom
                    </Badge>
                  )}
                </div>

                {/* Updated */}
                <div className="col-span-2 flex items-center gap-2 text-sm text-slate-500">
                  <Calendar className="h-4 w-4" />
                  {formatDate(pipeline.updated_at)}
                </div>

                {/* Actions */}
                <div className="col-span-2 flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditPipeline(pipeline)}
                  >
                    <Settings className="mr-1 h-3 w-3" />
                    Edit
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEditPipeline(pipeline)}>
                        <Settings className="mr-2 h-4 w-4" />
                        Edit Pipeline
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <Copy className="mr-2 h-4 w-4" />
                        Duplicate
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <Layers className="mr-2 h-4 w-4" />
                        Manage Stages
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-red-600">
                        <Archive className="mr-2 h-4 w-4" />
                        Archive
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PipelinesTab;
